import type { KnowledgeComponent, StudentLingoData } from "@/interfaces/lingo.interfaces";

export const NO_ACTIVITY_TEXT = "Getting started";

type ActivityThread = {
  name: string;
  type: "spelling" | "vocabulary" | "grammar";
  kcs: KnowledgeComponent[];
};

const getMaxTimestamp = (kcs: KnowledgeComponent[]) => {
  if (kcs.length === 0) return 0;

  return Math.max(
    ...kcs.map(kc =>
      kc.lastPracticedAt ? new Date(kc.lastPracticedAt as unknown as string).getTime() : 0
    )
  );
};

const hasBeenPractised = (kcs: KnowledgeComponent[]) =>
  kcs.some(kc => (kc.exposureCount || 0) > 0 || (!!kc.status && kc.status !== "not_yet_seen"));

const getTotalExposures = (kcs: KnowledgeComponent[]) =>
  kcs.reduce((total, kc) => total + (kc.exposureCount || 0), 0);

/**
 * Pick the thread the learner is actually on.
 *
 * Ordering matters. Recency alone is not enough: `lastPracticedAt` is unset for
 * most learners, and when every thread scores 0 a plain reduce keeps its
 * accumulator, so the FIRST populated thread won — always Spelling, because a
 * learner is handed a full spelling window before they touch anything. A learner
 * who had only ever done Grammar was reported as working on Spelling
 * (NLM-Test #143, still reproducing after #92).
 */
const selectActiveThread = (
  threads: ActivityThread[],
  currentActivityType: string | null | undefined
): ActivityThread => {
  // 1. The subject the learner is on, taken straight from their progress rather
  //    than inferred. This is the authoritative answer when it is available.
  const declared = threads.find(thread => thread.type === currentActivityType);
  if (declared) return declared;

  // 2. Otherwise the most recently practised thread, where a timestamp exists.
  const timestamped = threads.filter(thread => getMaxTimestamp(thread.kcs) > 0);
  if (timestamped.length > 0) {
    return timestamped.reduce((mostRecent, thread) =>
      getMaxTimestamp(thread.kcs) > getMaxTimestamp(mostRecent.kcs) ? thread : mostRecent
    );
  }

  // 3. No usable timestamps: prefer a thread the learner has demonstrably
  //    touched over an untouched window that merely happens to be listed first.
  const practised = threads.filter(thread => hasBeenPractised(thread.kcs));
  if (practised.length > 0) {
    return practised.reduce((most, thread) =>
      getTotalExposures(thread.kcs) > getTotalExposures(most.kcs) ? thread : most
    );
  }

  // 4. Nothing touched at all — keep the existing first-populated behaviour.
  return threads[0];
};

export function getWorkingOn(lingoData: StudentLingoData | undefined): string {
  if (!lingoData) return NO_ACTIVITY_TEXT;

  // Annotate before filtering: inferring the array through .filter() widens each
  // `type` to string, which no longer satisfies ActivityThread.
  const allThreads: ActivityThread[] = [
    { name: "Spelling", type: "spelling", kcs: lingoData.currentSpellingActivityKcs || [] },
    { name: "Vocabulary", type: "vocabulary", kcs: lingoData.currentVocabularyActivityKcs || [] },
    { name: "Grammar", type: "grammar", kcs: lingoData.currentGrammarActivityKcs || [] }
  ];
  const activityThreads = allThreads.filter(thread => thread.kcs.length > 0);

  if (activityThreads.length === 0) return NO_ACTIVITY_TEXT;

  const activeThread = selectActiveThread(activityThreads, lingoData.currentActivityType);

  const levels = [...new Set(activeThread.kcs.map(kc => kc.level))].sort((a, b) => a - b);
  if (levels.length === 0) return "-";

  const formatLevel = (level: number) => (level >= 7 ? "7+" : String(level));
  const min = levels[0];
  const max = levels[levels.length - 1];
  const levelText = min === max
    ? `Level ${formatLevel(min)}`
    : `Level ${formatLevel(min)}-${formatLevel(max)}`;

  return `${activeThread.name} (${levelText})`;
}
